import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  Spin,
  DatePicker,
  InputNumber,
  Space,
  Typography,
  Divider,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { getAllCustomers } from "../../services/customerService";
import { createInvoice } from "../../services/invoiceService";
import { INVOICE_TYPE_OPTIONS, UNIT_CODE_OPTIONS } from "../../constants/invoiceConstants";
import { formatPrice } from "../../utils/formatters";
import { toast } from "react-toastify";

const { TextArea } = Input;
const { Text } = Typography;

const CreateInvoiceModal = ({ visible, onCancel, onSuccess }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const watchedItems = Form.useWatch("items", form);
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (visible) {
      fetchCustomers();
      form.resetFields();
    }
  }, [visible, form]);

  const fetchCustomers = async () => {
    try {
      const customerList = await getAllCustomers(userId);
      setCustomers(customerList);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const calculateTotals = () => {
    const items = watchedItems || [];
    let subtotal = 0;
    let totalTax = 0;

    items.forEach((item) => {
      if (item) {
        const quantity = item.quantity || 0;
        const unitPrice = item.unitPrice || 0;
        const taxRate = item.taxRate || 0;
        const lineTotal = quantity * unitPrice;
        const lineTax = lineTotal * (taxRate / 100);
        subtotal += lineTotal;
        totalTax += lineTax;
      }
    });

    return { subtotal, totalTax, total: subtotal + totalTax };
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const invoiceData = {
        customerId: values.customerId,
        dueDate: values.dueDate ? values.dueDate.format("YYYY-MM-DD") : null,
        invoiceTypeCode: values.invoiceTypeCode || "SATIS",
        notes: values.notes,
        items: (values.items || []).map((item) => ({
          description: item.description,
          quantity: item.quantity,
          unitCode: item.unitCode || "ADET",
          unitPrice: item.unitPrice,
          taxRate: item.taxRate || 0,
        })),
      };

      await createInvoice(invoiceData);
      toast.success("Fatura başarıyla oluşturuldu");
      onSuccess();
    } catch (error) {
      if (!error.errorFields) {
        console.error("Error creating invoice:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();

  return (
    <Modal
      title="Yeni Fatura Oluştur"
      open={visible}
      onCancel={onCancel}
      maskClosable={false}
      width={900}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          İptal
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
        >
          Fatura Oluştur
        </Button>,
      ]}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ items: [{}] }}
        >
          <Form.Item
            name="customerId"
            label="Müşteri"
            rules={[{ required: true, message: "Lütfen bir müşteri seçin" }]}
          >
            <Select
              showSearch
              placeholder="Müşteri arayın ve seçin"
              filterOption={(input, option) =>
                (option?.label || "").toLowerCase().includes(input.toLowerCase())
              }
              options={customers.map((c) => ({
                value: c.id,
                label: c.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="invoiceTypeCode"
            label="Fatura Tipi"
            initialValue="SATIS"
          >
            <Select
              options={INVOICE_TYPE_OPTIONS}
              placeholder="Fatura tipi seçin"
            />
          </Form.Item>

          <Form.Item name="dueDate" label="Vade Tarihi">
            <DatePicker
              format="DD.MM.YYYY"
              placeholder="Vade tarihi seçin"
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Divider>Fatura Kalemleri</Divider>

          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space
                    key={key}
                    style={{ display: "flex", marginBottom: 8, flexWrap: "wrap", gap: 8 }}
                    align="start"
                  >
                    <Form.Item
                      {...restField}
                      name={[name, "description"]}
                      rules={[{ required: true, message: "Açıklama gerekli" }]}
                      style={{ minWidth: 200, flex: 2 }}
                    >
                      <Input placeholder="Açıklama" />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "quantity"]}
                      rules={[{ required: true, message: "Miktar gerekli" }]}
                      style={{ width: 110 }}
                    >
                      <InputNumber min={0.000001} step={1} placeholder="Miktar" style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "unitCode"]}
                      initialValue="ADET"
                      style={{ width: 110 }}
                    >
                      <Select
                        options={UNIT_CODE_OPTIONS}
                        placeholder="Birim"
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "unitPrice"]}
                      rules={[{ required: true, message: "Fiyat gerekli" }]}
                      style={{ width: 130 }}
                    >
                      <InputNumber
                        min={0}
                        step={0.01}
                        placeholder="Birim Fiyat"
                        style={{ width: "100%" }}
                        addonAfter="₺"
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "taxRate"]}
                      initialValue={20}
                      style={{ width: 100 }}
                    >
                      <InputNumber
                        min={0}
                        max={100}
                        placeholder="KDV %"
                        style={{ width: "100%" }}
                        addonAfter="%"
                      />
                    </Form.Item>
                    {fields.length > 1 && (
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    )}
                  </Space>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                  >
                    Kalem Ekle
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <div style={{ textAlign: "right", marginBottom: 16 }}>
            <Space direction="vertical" size={4}>
              <Text>Ara Toplam: {formatPrice(totals.subtotal)}</Text>
              <Text>KDV: {formatPrice(totals.totalTax)}</Text>
              <Text strong style={{ fontSize: 16 }}>
                Genel Toplam: {formatPrice(totals.total)}
              </Text>
            </Space>
          </div>

          <Form.Item name="notes" label="Notlar">
            <TextArea rows={3} placeholder="Fatura notları" />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default CreateInvoiceModal;
